import { useEffect, useState } from "react";

// Free, key-less endpoint (attribution link lives in the footer).
const API_URL = "https://open.er-api.com/v6/latest/EUR";
const STORAGE_KEY = "vb_eur_rate";

// Local calendar day as "YYYY-MM-DD" - the cache key that limits us to one
// request per day.
const todayKey = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const isValidRate = (rate) => typeof rate === "number" && isFinite(rate) && rate > 0;

const readCache = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return cached && isValidRate(cached.rate) ? cached : null;
  } catch {
    return null;
  }
};

const writeCache = (entry) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Storage unavailable (private mode / quota) - the rate still works for
    // this session, we just cannot cache it.
  }
};

/**
 * Returns how many RSD equal 1 EUR, or `null` while no rate is known yet.
 *
 * The rate is fetched at most once per calendar day and cached in
 * localStorage. If the request fails, the last known rate is kept.
 */
const useEurRate = () => {
  const [rate, setRate] = useState(() => readCache()?.rate ?? null);

  useEffect(() => {
    const cached = readCache();
    if (cached && cached.date === todayKey()) return; // already converted today

    let cancelled = false;

    fetch(API_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`Exchange rate request failed: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const fresh = data?.rates?.RSD;
        if (!isValidRate(fresh)) throw new Error("Exchange rate response has no RSD rate");
        if (cancelled) return;
        writeCache({ date: todayKey(), rate: fresh });
        setRate(fresh);
      })
      .catch(() => {
        // Fall back to the last known rate, which is already in state.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return rate;
};

export default useEurRate;
